import { callApi, HTTPMethods } from '@/utils/api';
import KeycloakService from './keycloakService';
import { TypeNotification, useGlobalStore } from '@/store/globalStore';
// @ts-ignore
import SrhUiI18nService from 'srh-ui/dist/services/i18n';
import { getApiUrl } from '@/utils/url';
import type { LienResponse, CreateLienDTO, UpdateLienDTO } from '@/types/api';

export default {
  /** GET /liens/user/:userId → LienResponse[] */
  async loadUserLinks(
    userId: string,
    onSuccess?: (links: LienResponse[]) => void,
    onError?: (err: unknown) => void
  ) {
    const i18n = new SrhUiI18nService();
    const { t } = i18n.getI18nInstance().global;
    const globalStore = useGlobalStore();

    try {
      await KeycloakService.CallUpdate();
      const config = {
        'Content-Type': 'application/json',
      };

      await callApi({
        path: `${getApiUrl()}/liens/user/${userId}`,
        method: HTTPMethods.GET,
        options: config,
        successCallback: (result: LienResponse[]) => {
          onSuccess?.(result);
        },
        errorMessage: t('errors.userLinks'),
      });
    } catch (e: unknown) {
      onError?.(e);
      if (!onError) {
        globalStore.showNotification(
          TypeNotification.ERROR,
          t('errors.unknown'),
          t('errors.title')
        );
      }
    }
  },

  /** POST /liens with CreateLienDTO (FormData with name, url, userId, file) */
  async createLink(
    linkData: CreateLienDTO & { file?: File },
    onSuccess?: (result: LienResponse) => void,
    onError?: (err: unknown) => void
  ) {
    const i18n = new SrhUiI18nService();
    const { t } = i18n.getI18nInstance().global;
    const globalStore = useGlobalStore();

    try {
      await KeycloakService.CallUpdate();

      // Use FormData to handle file upload
      const formData = new FormData();
      formData.append('name', linkData.name);
      formData.append('url', linkData.url);
      formData.append('userId', linkData.userId);
      if (linkData.file) {
        formData.append('file', linkData.file);
      }

      await callApi({
        path: `${getApiUrl()}/liens`,
        method: HTTPMethods.POST,
        body: formData,
        options: {},
        successCallback: (result: any) => {
          globalStore.showNotification(
            TypeNotification.SUCCESS,
            t('messages.linkCreated'),
            t('messages.success')
          );
          onSuccess?.(result);
        },
        errorMessage: t('errors.createLink'),
      });
    } catch (e: any) {
      onError?.(e) ??
        globalStore.showNotification(
          TypeNotification.ERROR,
          t('errors.unknown'),
          t('errors.title')
        );
    }
  },

  /** PATCH /liens/:id with UpdateLienDTO (FormData) */
  async updateLink(
    linkId: string,
    linkData: UpdateLienDTO & { file?: File },
    onSuccess?: (result: LienResponse) => void,
    onError?: (err: unknown) => void
  ) {
    const i18n = new SrhUiI18nService();
    const { t } = i18n.getI18nInstance().global;
    const globalStore = useGlobalStore();

    try {
      await KeycloakService.CallUpdate();

      const formData = new FormData();
      if (linkData.name) formData.append('name', linkData.name);
      if (linkData.url) formData.append('url', linkData.url);
      if (linkData.userId) formData.append('userId', linkData.userId);
      if (linkData.file) formData.append('file', linkData.file);

      await callApi({
        path: `${getApiUrl()}/liens/${linkId}`,
        method: HTTPMethods.PATCH,
        body: formData,
        options: {},
        successCallback: (result: any) => {
          globalStore.showNotification(
            TypeNotification.SUCCESS,
            t('messages.linkUpdated'),
            t('messages.success')
          );
          onSuccess?.(result);
        },
        errorMessage: t('errors.updateLink'),
      });
    } catch (e: any) {
      onError?.(e) ??
        globalStore.showNotification(
          TypeNotification.ERROR,
          t('errors.unknown'),
          t('errors.title')
        );
    }
  },

  /** DELETE /liens/:id */
  async deleteLink(
    linkId: string,
    onSuccess?: () => void,
    onError?: (err: unknown) => void
  ) {
    const i18n = new SrhUiI18nService();
    const { t } = i18n.getI18nInstance().global;
    const globalStore = useGlobalStore();

    try {
      await KeycloakService.CallUpdate();
      const config = {
        'Content-Type': 'application/json',
      };

      await callApi({
        path: `${getApiUrl()}/liens/${linkId}`,
        method: HTTPMethods.DELETE,
        options: config,
        successCallback: () => {
          globalStore.showNotification(
            TypeNotification.SUCCESS,
            t('messages.linkDeleted'),
            t('messages.success')
          );
          onSuccess?.();
        },
        errorMessage: t('errors.deleteLink'),
      });
    } catch (e: any) {
      onError?.(e) ??
        globalStore.showNotification(
          TypeNotification.ERROR,
          t('errors.unknown'),
          t('errors.title')
        );
    }
  },
};
